import { Button, Table, Checkbox, Dropdown, theme } from 'antd';
import {
  PlusOutlined,
  MoreOutlined,
  CheckOutlined,
  EditOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { BankAccountResponse } from '@/types/api';
import type { BaseItemPermissions } from '@/utils/permissionUtils';
import { controlHeightTokens } from '@/styles/tokens';
import TableContainer from '@/components/common/TableContainer';
import { getToggleStatusStyle } from '@/hooks/useVendorPermissions';

interface BankAccountsSectionProps {
  bankAccounts: BankAccountResponse[];
  loading: boolean;
  includeInactive: boolean;
  onIncludeInactiveChange: (checked: boolean) => void;
  canEdit: boolean;
  canAdd: boolean;
  getItemPermissions: (item: BankAccountResponse) => BaseItemPermissions;
  onAdd: () => void;
  onView: (account: BankAccountResponse) => void;
  onEdit: (account: BankAccountResponse) => void;
  onSetDefault: (account: BankAccountResponse) => void;
  onToggleStatus: (account: BankAccountResponse) => void;
  addressLang?: string | null;
}

export default function BankAccountsSection({
  bankAccounts,
  loading,
  includeInactive,
  onIncludeInactiveChange,
  canEdit: _canEdit,
  canAdd,
  getItemPermissions,
  onAdd,
  onView,
  onEdit,
  onSetDefault,
  onToggleStatus,
  addressLang,
}: BankAccountsSectionProps) {
  const { t } = useTranslation('vendors');
  const { token } = theme.useToken();

  const filteredAndSortedBankAccounts = bankAccounts
    ?.filter((b) => includeInactive || b.isActive)
    ?.slice()
    ?.sort((a, b) => {
      // Default first
      if (a.isDefault !== b.isDefault) {
        return a.isDefault ? -1 : 1;
      }
      // Then by bankId (newer items have higher IDs)
      return b.bankId - a.bankId;
    });

  return (
    <div>
      <div className="tw-mb-4 tw-flex tw-justify-between tw-items-center">
        <h3
          style={{
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
            margin: 0,
          }}
        >
          {t('vendor.sections.bankAccounts')}
        </h3>
        <div className="tw-flex tw-items-center tw-gap-4">
          <Checkbox checked={includeInactive} onChange={(e) => onIncludeInactiveChange(e.target.checked)}>
            {t('vendor.labels.includeInactiveBankAccounts')}
          </Checkbox>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canAdd}
            onClick={onAdd}
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          >
            {t('vendor.modals.add')}
          </Button>
        </div>
      </div>
      <TableContainer>
        <Table<BankAccountResponse>
          dataSource={filteredAndSortedBankAccounts}
          rowKey="bankId"
          rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
          onRow={(record) => ({
            onClick: () => onView(record),
            style: { cursor: 'pointer' },
          })}
          loading={loading}
          pagination={{
            pageSize: 10,
          }}
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: t('vendor.table.default'),
              dataIndex: 'isDefault',
              key: 'isDefault',
              width: 100,
              align: 'center',
              showSorterTooltip: false,
              render: (isDefault: boolean) =>
                isDefault ? (
                  <CheckOutlined style={{ color: token.colorPrimary, fontSize: token.sizeMD }} />
                ) : null,
            },
            {
              title: t('vendor.table.accountName'),
              dataIndex: 'accountName',
              key: 'accountName',
              sorter: (a, b) => (a.accountName || '').localeCompare(b.accountName || ''),
              showSorterTooltip: false,
            },
            {
              title: t('vendor.table.bankCode'),
              dataIndex: 'bankCode',
              key: 'bankCode',
              sorter: (a, b) => (a.bankCode || '').localeCompare(b.bankCode || ''),
              showSorterTooltip: false,
              render: (text: string | undefined) => text || '-',
            },
            {
              title: t('vendor.table.bankName'),
              dataIndex: 'bankName',
              key: 'bankName',
              sorter: (a, b) => (a.bankName || '').localeCompare(b.bankName || ''),
              showSorterTooltip: false,
            },
            {
              title: t('vendor.table.branchName'),
              dataIndex: 'branchName',
              key: 'branchName',
              sorter: (a, b) => (a.branchName || '').localeCompare(b.branchName || ''),
              showSorterTooltip: false,
              render: (text: string | null) => text || '-',
            },
            {
              title: t('vendor.table.currency'),
              dataIndex: 'currency',
              key: 'currency',
              sorter: (a, b) => (a.currency || '').localeCompare(b.currency || ''),
              showSorterTooltip: false,
            },
            {
              title: t('vendor.table.accountNumber'),
              dataIndex: 'accountNumber',
              key: 'accountNumber',
              sorter: (a, b) => (a.accountNumber || '').localeCompare(b.accountNumber || ''),
              showSorterTooltip: false,
            },
            {
              title: t('vendor.table.swiftCode'),
              dataIndex: 'swiftCode',
              key: 'swiftCode',
              sorter: (a, b) => (a.swiftCode || '').localeCompare(b.swiftCode || ''),
              showSorterTooltip: false,
              render: (text: string | null) => text || '-',
            },
            {
              title: t('vendor.table.iban'),
              dataIndex: 'iban',
              key: 'iban',
              sorter: (a, b) => (a.iban || '').localeCompare(b.iban || ''),
              showSorterTooltip: false,
              render: (text: string | null) => text || '-',
            },
            {
              title: t('vendor.table.bankLocation'),
              key: 'bankLocation',
              sorter: (a, b) => {
                // Sort using the correct language based on vendor's addressLang
                const useZh = addressLang?.startsWith('zh');
                const aName = useZh
                  ? a.countryNameZh || a.countryNameEn || ''
                  : a.countryNameEn || a.countryNameZh || '';
                const bName = useZh
                  ? b.countryNameZh || b.countryNameEn || ''
                  : b.countryNameEn || b.countryNameZh || '';
                return aName.localeCompare(bName);
              },
              showSorterTooltip: false,
              render: (_: unknown, record: BankAccountResponse) => {
                // Display country name based on vendor's addressLang
                const useZh = addressLang?.startsWith('zh');
                const countryName = useZh
                  ? record.countryNameZh || record.countryNameEn
                  : record.countryNameEn || record.countryNameZh;
                return countryName || '-';
              },
            },
            {
              title: t('vendor.table.notes'),
              dataIndex: 'notes',
              key: 'notes',
              sorter: (a, b) => (a.notes || '').localeCompare(b.notes || ''),
              showSorterTooltip: false,
              render: (text: string | null) => text || '-',
            },
            {
              title: t('vendor.table.actions'),
              key: 'actions',
              width: 80,
              fixed: 'right',
              onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
              render: (_: unknown, record: BankAccountResponse) => {
                const itemPerms = getItemPermissions(record);
                return (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'setDefault',
                          label: t('vendor.actions.setAsDefault'),
                          icon: <CheckOutlined />,
                          disabled: !itemPerms.canSetDefault,
                          onClick: () => onSetDefault(record),
                        },
                        {
                          key: 'edit',
                          label: t('vendor.actions.edit'),
                          icon: <EditOutlined />,
                          disabled: !itemPerms.canEdit,
                          onClick: () => onEdit(record),
                        },
                        {
                          type: 'divider',
                        },
                        {
                          key: 'toggleStatus',
                          label: itemPerms.isItemActive
                            ? t('vendor.actions.deactivate')
                            : t('vendor.actions.activate'),
                          icon: itemPerms.isItemActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                          ...getToggleStatusStyle(itemPerms, token),
                          onClick: () => onToggleStatus(record),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                );
              },
            },
          ]}
        />
      </TableContainer>
    </div>
  );
}
